/**
 * Controls for a lesson room layout: preset, panel side, model size, desk
 * distance and, per lesson, the main model and true sizes. Used by the Layout
 * tab (one lesson) and the Default lesson layout page (every lesson without its
 * own), with a live preview from the same placement the player uses.
 */

import { useMemo } from 'react';

import { Input } from '@/Components/ui/input';
import { Label } from '@/Components/ui/label';
import { cn } from '@/lib/utils';
import {
  LAYOUT_PRESETS,
  PRESET_SPECS,
  resolveLessonLayout,
  type LessonLayout,
} from '../../../lib/three/lessonLayout';
import { LayoutPreview, type RoomContext } from './LayoutPreview';
import { Shuffle } from 'lucide-react';
import { THEME_IDS, THEME_LABELS, themeFor } from '../../../lib/three/roomTheme';

export type LayoutValue = Partial<LessonLayout>;

interface Props {
  /** What is stored: only the fields this level sets. */
  value: LayoutValue;
  onChange: (next: LayoutValue) => void;
  /** The level below (the global default, for a lesson); fills what `value` leaves unset. */
  inherited?: unknown;
  /** The lesson's models. Empty on the default page, where per-model fields do not apply. */
  assets: { id: string; name: string }[];
  readOnly?: boolean;
  /** The lesson the room is for: its subject and grade pick the automatic theme. */
  room?: RoomContext;
}

const SIDES: { id: LessonLayout['panelSide']; label: string }[] = [
  { id: 'auto', label: 'Preset' },
  { id: 'left', label: 'Left' },
  { id: 'right', label: 'Right' },
];

export function LayoutEditor({ value, onChange, inherited, assets, readOnly, room }: Props) {
  const perLesson = assets.length > 0;
  const resolved = useMemo(() => resolveLessonLayout(value, inherited), [value, inherited]);
  const set = (patch: LayoutValue) => onChange({ ...value, ...patch });
  const gallery = resolved.preset === 'gallery';
  const autoTheme = themeFor({ subject: room?.subject, classNumber: room?.classNumber, preset: resolved.preset });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="space-y-6">
        <section className="space-y-2">
          <Label>Room preset</Label>
          <div className="grid grid-cols-2 gap-2">
            {LAYOUT_PRESETS.map((preset) => {
              const spec = PRESET_SPECS[preset];
              const active = resolved.preset === preset;
              return (
                <button
                  key={preset}
                  type="button"
                  disabled={readOnly}
                  onClick={() => set({ preset })}
                  aria-pressed={active}
                  className={cn(
                    'rounded-lg border px-3 py-2 text-left transition-colors disabled:opacity-60',
                    active ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/50'
                  )}
                >
                  <div className="text-sm font-medium text-foreground">{spec.label}</div>
                  <div className="text-xs text-muted-foreground">{spec.purpose}</div>
                </button>
              );
            })}
          </div>
        </section>

        <section className="space-y-2">
          <Label>Lesson panel side</Label>
          <div className="inline-flex rounded-lg border border-border p-0.5" role="group">
            {SIDES.map((side) => (
              <button
                key={side.id}
                type="button"
                disabled={readOnly || gallery}
                onClick={() => set({ panelSide: side.id })}
                aria-pressed={resolved.panelSide === side.id}
                className={cn(
                  'px-3 py-1 text-sm rounded-md disabled:opacity-50',
                  resolved.panelSide === side.id ? 'bg-primary/15 text-foreground' : 'text-muted-foreground'
                )}
              >
                {side.label}
              </button>
            ))}
          </div>
          {gallery && <p className="text-xs text-muted-foreground">Gallery keeps the panel straight ahead, above the models.</p>}
        </section>

        <section className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="layout-model-scale">Model size · {Math.round(resolved.modelScale * 100)}%</Label>
            <input
              id="layout-model-scale"
              type="range"
              min={0.7}
              max={1.5}
              step={0.05}
              disabled={readOnly}
              value={resolved.modelScale}
              onChange={(e) => set({ modelScale: Number(e.target.value) })}
              className="w-full accent-primary"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="layout-reach">
              Desk distance · {resolved.deskReach == null ? `preset (${PRESET_SPECS[resolved.preset].reach} m)` : `${resolved.deskReach.toFixed(2)} m`}
            </Label>
            <input
              id="layout-reach"
              type="range"
              min={0.6}
              max={0.95}
              step={0.05}
              disabled={readOnly}
              value={resolved.deskReach ?? PRESET_SPECS[resolved.preset].reach}
              onChange={(e) => set({ deskReach: Number(e.target.value) })}
              className="w-full accent-primary"
            />
          </div>
        </section>

        <section className="space-y-2">
          <Label htmlFor="layout-theme">Room around the desk</Label>
          <select
            id="layout-theme"
            disabled={readOnly}
            value={resolved.roomTheme}
            onChange={(e) => set({ roomTheme: e.target.value })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="auto">
              Automatic{room?.subject ? ` — ${THEME_LABELS[autoTheme.id]}` : ' (from the lesson subject)'}
            </option>
            {THEME_IDS.map((id) => (
              <option key={id} value={id}>
                {THEME_LABELS[id]}
              </option>
            ))}
          </select>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                disabled={readOnly}
                checked={resolved.showProps}
                onChange={(e) => set({ showProps: e.target.checked })}
                className="accent-primary"
              />
              Show themed pieces around the desk
            </label>
            {perLesson && resolved.showProps && (
              <button
                type="button"
                disabled={readOnly}
                onClick={() => set({ propSeed: resolved.propSeed + 1 })}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted disabled:opacity-50"
              >
                <Shuffle className="h-3.5 w-3.5" />
                Shuffle arrangement
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Pieces stand beside the desk, never on it, behind the models or in front of the lesson panel. Each lesson gets its
            own arrangement; everyone in a class sees the same room.
          </p>
        </section>

        {perLesson && (
          <section className="space-y-2">
            <Label htmlFor="layout-primary">Main model</Label>
            <select
              id="layout-primary"
              disabled={readOnly}
              value={resolved.primaryAssetId ?? assets[0]?.id ?? ''}
              onChange={(e) => set({ primaryAssetId: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {assets.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">Stands in the middle of the front row; larger in Focus.</p>
          </section>
        )}

        <section className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              disabled={readOnly}
              checked={resolved.realScale}
              onChange={(e) => set({ realScale: e.target.checked })}
              className="accent-primary"
            />
            Keep true size ratios between models
          </label>
          {resolved.realScale && perLesson && (
            <div className="space-y-2 rounded-lg border border-border p-3">
              <p className="text-xs text-muted-foreground">
                Real largest dimension of each model. Models left blank keep the standard size. Anything too small to see is
                enlarged and the room says "not to scale".
              </p>
              {assets.map((a) => (
                <div key={a.id} className="flex items-center gap-2">
                  <span className="flex-1 truncate text-sm">{a.name}</span>
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    disabled={readOnly}
                    className="w-28"
                    value={value.assetSizesCm?.[a.id] ?? ''}
                    onChange={(e) => {
                      const sizes = { ...(value.assetSizesCm ?? {}) };
                      const cm = Number(e.target.value);
                      if (e.target.value === '' || !(cm > 0)) delete sizes[a.id];
                      else sizes[a.id] = cm;
                      set({ assetSizesCm: sizes });
                    }}
                  />
                  <span className="text-xs text-muted-foreground">cm</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <LayoutPreview layout={resolved} assets={perLesson ? assets : SAMPLE_ASSETS} room={room} />
    </div>
  );
}

/** Three stand-in models for the default page, which has no lesson of its own. */
const SAMPLE_ASSETS = [
  { id: 'sample_1', name: 'Model 1' },
  { id: 'sample_2', name: 'Model 2' },
  { id: 'sample_3', name: 'Model 3' },
];
